import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import useUnreadChatCount from '../hooks/useUnreadChatCount';

/**
 * FloatingMessagesFAB (iter95)
 *
 * Positioned ABOVE the Profile FAB. Same size and shape as the rest of the
 * stack (12x12 rounded-full). GenzoX brand green (#00bf63). Red unread
 * badge shown when there are new chat messages. Tapping navigates to /chat.
 *
 * Stack vertical positions (from bottom-nav upward):
 *   Back-to-top  bottom-3     (12px)
 *   Contact Us   bottom-[68px]
 *   Profile      bottom-[124px]
 *   Messages     bottom-[180px]  ← this component
 */

const FloatingMessagesFAB = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { unreadConvos, unreadMessages } = useUnreadChatCount(user);

  const handleClick = () => {
    // Same target as the old top-nav Messages icon — /chat handles the
    // "not signed in" case with its own redirect.
    navigate(user ? '/chat' : '/login?next=/chat');
  };

  const showBadge = !!user && unreadConvos > 0;

  return (
    <button
      type="button"
      data-testid="floating-messages-fab"
      aria-label={showBadge ? `Messages, ${unreadMessages} unread` : 'Messages'}
      onClick={handleClick}
      className="fixed bottom-[180px] right-3 z-40 w-12 h-12 rounded-full shadow-xl flex items-center justify-center text-white transition-transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-4 focus:ring-[#00bf63]/30"
      style={{ background: 'linear-gradient(135deg, #00bf63 0%, #00a856 100%)' }}
    >
      <MessageCircle size={22} strokeWidth={2.2} />
      {showBadge && (
        <span
          data-testid="floating-messages-unread-badge"
          className="absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-md ring-2 ring-white"
        >
          {unreadMessages > 99 ? '99+' : unreadMessages}
        </span>
      )}
    </button>
  );
};

export default FloatingMessagesFAB;
